import api from '../api';

export type RubricCriterionKey =
  | 'calidad'
  | 'contribucion'
  | 'relevancia'
  | 'pertinencia'
  | 'atencionUsuariosBeneficiarios'
  | 'estiloPresentacion';

export type EvaluatorOwnEvaluationStatus = 'EMPTY' | 'DRAFT' | 'SUBMITTED';
export type EvaluatorDecision = 'APPROVED' | 'OBSERVATIONS' | 'REJECTED';

export interface EvaluatorRubricData {
  calidad?: number | null;
  contribucion?: number | null;
  relevancia?: number | null;
  pertinencia?: number | null;
  atencionUsuariosBeneficiarios?: number | null;
  estiloPresentacion?: number | null;
}

export interface EvaluatorAssignmentSummary {
  assignmentId: string;
  evaluationId: string;
  roundNumber: number;
  evaluationStatus: string;
  ownEvaluationStatus: EvaluatorOwnEvaluationStatus;
  isSubmitted: boolean;
  folio: string;
  tituloContribucion: string | null;
  eventName: string;
  hasEvaluableDocument: boolean;
  evaluatedPaperVersionId: string | null;
}

export interface EvaluatorAssignmentDetail extends EvaluatorAssignmentSummary {
  registrationId: string;
  eventId: string;
  evaluator: {
    id: string;
    name: string;
    email: string;
    institution: string | null;
  };
}

export interface EvaluatorEvaluationPayload {
  status: EvaluatorOwnEvaluationStatus;
  isSubmitted: boolean;
  rubricData: EvaluatorRubricData | null;
  evaluatedPaperVersionId: string | null;
  observationComment: string | null;
  decision: EvaluatorDecision | null;
  totalScore: number | null;
  submittedAt: string | null;
}

export interface SaveDraftPayload {
  rubricData?: EvaluatorRubricData;
  observationComment?: string;
  evaluatedPaperVersionId?: string;
}

export interface SubmitEvaluationPayload {
  rubricData: Required<Record<RubricCriterionKey, number>>;
  observationComment?: string;
  evaluatedPaperVersionId: string;
}

export interface SaveDraftResponse {
  success: boolean;
  status?: EvaluatorOwnEvaluationStatus;
  isSubmitted?: boolean;
  evaluation?: EvaluatorEvaluationPayload;
}

export interface SubmitEvaluationResponse {
  success: boolean;
  decision: EvaluatorDecision;
  totalScore: number;
  message: string;
}

function buildEvaluatorHeaders(token: string) {
  return {
    'x-evaluator-token': token,
  };
}

export async function listEvaluatorAssignments(token: string): Promise<EvaluatorAssignmentSummary[]> {
  const { data } = await api.get('/evaluator/assignments', {
    headers: buildEvaluatorHeaders(token),
  });

  if (Array.isArray(data)) {
    return data as EvaluatorAssignmentSummary[];
  }

  if (Array.isArray(data?.items)) {
    return data.items as EvaluatorAssignmentSummary[];
  }

  return [];
}

export async function getEvaluatorAssignmentDetail(token: string, assignmentId: string): Promise<EvaluatorAssignmentDetail> {
  const { data } = await api.get(`/evaluator/assignments/${encodeURIComponent(assignmentId)}`, {
    headers: buildEvaluatorHeaders(token),
  });

  return data as EvaluatorAssignmentDetail;
}

export async function getEvaluatorEvaluation(token: string, assignmentId: string): Promise<EvaluatorEvaluationPayload> {
  const { data } = await api.get(`/evaluator/assignments/${encodeURIComponent(assignmentId)}/evaluation`, {
    headers: buildEvaluatorHeaders(token),
  });

  return data as EvaluatorEvaluationPayload;
}

export async function saveEvaluatorDraft(
  token: string,
  assignmentId: string,
  payload: SaveDraftPayload,
): Promise<SaveDraftResponse> {
  const { data } = await api.post(`/evaluator/assignments/${encodeURIComponent(assignmentId)}/evaluation`, payload, {
    headers: buildEvaluatorHeaders(token),
  });

  return data as SaveDraftResponse;
}

export async function submitEvaluatorEvaluation(
  token: string,
  assignmentId: string,
  payload: SubmitEvaluationPayload,
): Promise<SubmitEvaluationResponse> {
  const { data } = await api.post(`/evaluator/assignments/${encodeURIComponent(assignmentId)}/evaluation/submit`, payload, {
    headers: buildEvaluatorHeaders(token),
  });

  return data as SubmitEvaluationResponse;
}

export async function getEvaluatorDocument(token: string, assignmentId: string): Promise<Blob> {
  const response = await api.get(`/assignments/${encodeURIComponent(assignmentId)}/paper/document`, {
    headers: buildEvaluatorHeaders(token),
    responseType: 'blob',
  });

  if (response.data instanceof Blob) {
    return response.data;
  }

  return new Blob([response.data], {
    type: response.headers['content-type'] || 'application/pdf',
  });
}
